<template>
  <div class="add-credits">
    <!-- Header with toggle -->
    <header class="add-credits__head">
      <div class="add-credits__head-left">
        <div class="add-credits__icon">
          <i class="fa-regular fa-coins" />
        </div>
        <div>
          <h3 class="add-credits__title">
            {{ t('billing.addCredits.title') }}
          </h3>
          <p class="add-credits__subtitle">
            {{ t('billing.addCredits.subtitle') }}
          </p>
        </div>
      </div>
      <InputSwitch
        :model-value="enabled"
        @update:model-value="onToggle"
      />
    </header>

    <transition name="add-credits-expand">
      <div
        v-if="enabled"
        class="add-credits__body"
      >
        <!-- Centered amount stepper (shared component) -->
        <AmountStepper
          :model-value="modelValue"
          :steps="STEP_VALUES"
          :label="t('billing.addCredits.amountLabel')"
          :suffix="`${currencySymbol} / mois`"
          :max="10000"
          :increment-label="t('billing.addCredits.increment')"
          :decrement-label="t('billing.addCredits.decrement')"
          class="add-credits__stepper"
          @update:model-value="onAmountChange"
        />

        <!-- Estimation panel with embedded country dropdown -->
        <div
          v-if="selectedCountryData && modelValue > 0"
          class="add-credits__estim"
        >
          <div class="add-credits__estim-head">
            <div class="add-credits__estim-eyebrow">
              {{ t('billing.addCredits.estimationFor', { country: countryLabel(selectedCountry) }) }}
            </div>
            <Dropdown
              :model-value="selectedCountry"
              :options="countryOptions"
              option-label="label"
              option-value="value"
              filter
              :filter-placeholder="t('billing.addCredits.searchCountry')"
              :auto-filter-focus="true"
              :reset-filter-on-hide="true"
              class="add-credits__country"
              @update:model-value="onCountryChange"
            >
              <template #value="slotProps">
                <span v-if="slotProps.value">
                  {{ flagEmoji(slotProps.value) }} {{ countryLabel(slotProps.value) }}
                </span>
              </template>
              <template #option="slotProps">
                <span>
                  {{ flagEmoji(slotProps.option.value) }} {{ slotProps.option.label }}
                </span>
              </template>
            </Dropdown>
          </div>

          <div class="add-credits__estim-quantity">
            <div class="add-credits__estim-sms-icon">
              <i class="fa-regular fa-message-sms" />
            </div>
            <div class="add-credits__estim-qty-block">
              <div class="add-credits__estim-qty-row">
                <span class="add-credits__estim-qty-num">
                  ~ {{ formatNumber(estimatedQuantity) }}
                </span>
                <span class="add-credits__estim-sms-pill">SMS</span>
              </div>
              <div class="add-credits__estim-qty-caption">
                {{ t('billing.addCredits.qtyCaption') }}
              </div>
            </div>
          </div>

          <div class="add-credits__estim-prices">
            <div class="add-credits__estim-price">
              <div class="add-credits__estim-price-label">
                {{ t('billing.addCredits.inPlanLabel') }}
              </div>
              <div class="add-credits__estim-price-value add-credits__estim-price-value--inplan">
                {{ formatUnitPrice(selectedCountryData.unitPriceInPlan) }}
                <span class="add-credits__estim-price-unit">/ SMS</span>
              </div>
            </div>

            <div class="add-credits__estim-vs">vs</div>

            <div class="add-credits__estim-price">
              <div class="add-credits__estim-price-label">
                {{ t('billing.addCredits.oneShootLabel') }}
              </div>
              <div class="add-credits__estim-price-value add-credits__estim-price-value--strike">
                {{ formatUnitPrice(selectedCountryData.unitPriceOneShoot) }}
                <span class="add-credits__estim-price-unit">/ SMS</span>
              </div>
            </div>

            <div
              v-if="selectedCountryData.savingsRatio > 0"
              class="add-credits__estim-saving"
            >
              <i class="fa-regular fa-piggy-bank" />
              <span>
                {{ t('billing.addCredits.savings', {
                  percent: Math.round(selectedCountryData.savingsRatio * 100),
                }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Info note -->
        <div class="add-credits__note">
          <i class="fa-regular fa-circle-info" />
          <span>{{ t('billing.addCredits.note') }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import InputSwitch from 'primevue/inputswitch';
import Dropdown from 'primevue/dropdown';
import AmountStepper from '@/components/billing/dialogs/AmountStepper.vue';
import { fetchSmsPricing } from '@/composables/billing/billingApi';
import { SmsPricingResponse } from '@/types/billing-types';

const STEP_VALUES = [
  10, 25, 50, 75, 100, 125, 150, 200,
  500, 1000, 2000, 3000, 5000, 10000,
];

const FLAGS: Record<string, string> = {
  FR: '🇫🇷',
  BE: '🇧🇪',
  ES: '🇪🇸',
  IT: '🇮🇹',
  GB: '🇬🇧',
  US: '🇺🇸',
};

export default defineComponent({
  name: 'AddCreditsStep',

  components: { InputSwitch, Dropdown, AmountStepper },

  props: {
    modelValue: {
      type: Number as PropType<number>,
      default: 0,
    },
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
    /**
     * Plan that the shop is about to switch to in the current wizard flow
     * (passed by ChangePlanDialog from its selectedPlan ref). When set, the
     * SMS pricing grid is fetched with this tier instead of the shop's CURRENT
     * plan — otherwise the « tarif à l'unité » displayed (and the « X %
     * d'économie » derived from it) would reflect the wrong tier.
     */
    targetPlan: {
      type: String as PropType<string | null>,
      default: null,
    },
    /**
     * Monthly SMS amount the shop is currently subscribed to (`plan.smsPlan`
     * mirrored from the overview). When > 0 and `modelValue` is 0 (no value
     * yet picked by the user), the toggle opens activated and the amount is
     * pre-filled. The customer can then keep / decrease / disable / increase.
     * Spec V4-d & SMS rule: no prorata, only the positive delta is invoiced.
     */
    initialSmsInclusion: {
      type: Number as PropType<number>,
      default: 0,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const smsPricing = ref<SmsPricingResponse | null>(null);
    // Pre-fill from `initialSmsInclusion` when the wizard arrives in this
    // phase with no user-picked value yet (modelValue still 0). Keeps the
    // user's choice if they've already interacted with the toggle.
    const initialFromCurrent = props.modelValue === 0 && props.initialSmsInclusion > 0
      ? props.initialSmsInclusion
      : 0;
    if (initialFromCurrent > 0) {
      emit('update:modelValue', initialFromCurrent);
    }
    const enabled = ref(props.modelValue > 0 || initialFromCurrent > 0);
    // Default to the shop's home country (read from the overview) before
    // falling back to FR. The dropdown remains user-driven afterwards.
    const selectedCountry = ref<string>(
      store.state.billing.overview?.shop?.country ?? 'FR',
    );

    const currencySymbol = computed(() => (
      props.currency === 'USD' ? '$' : '€'
    ));

    const countryOptions = computed(() => (
      smsPricing.value?.countries.map((c) => ({
        value: c.country,
        label: c.countryLabel,
      })) ?? []
    ));

    const selectedCountryData = computed(() => (
      smsPricing.value?.countries.find((c) => c.country === selectedCountry.value) ?? null
    ));

    const estimatedQuantity = computed(() => {
      const c = selectedCountryData.value;
      if (!c || !props.modelValue || c.unitPriceInPlan <= 0) return 0;
      return Math.floor(props.modelValue / c.unitPriceInPlan);
    });

    const flagEmoji = (code: string) => FLAGS[code] ?? '🌐';

    const countryLabel = (code: string) => (
      smsPricing.value?.countries.find((c) => c.country === code)?.countryLabel ?? code
    );

    const formatUnitPrice = (n: number) => {
      const trimmed = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return `${trimmed} ${currencySymbol.value}`;
    };

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    const onAmountChange = (v: number | null | undefined) => {
      const value = v == null || v < 0 ? 0 : Number(v);
      emit('update:modelValue', value);
    };

    const onCountryChange = (v: string) => {
      selectedCountry.value = v;
    };

    const onToggle = (v: boolean) => {
      enabled.value = v;
      if (!v) emit('update:modelValue', 0);
      else if (props.modelValue === 0) emit('update:modelValue', 25);
    };

    // Refetch pricing whenever the wizard-selected plan changes (or on
    // initial mount when no targetPlan is provided). `immediate: true` covers
    // both cases without a separate onMounted hook.
    watch(
      () => props.targetPlan,
      async (plan) => {
        smsPricing.value = await fetchSmsPricing(
          plan ? { planOverride: plan } : {},
        );
      },
      { immediate: true },
    );

    return {
      t,
      STEP_VALUES,
      smsPricing,
      enabled,
      selectedCountry,
      currencySymbol,
      countryOptions,
      selectedCountryData,
      estimatedQuantity,
      flagEmoji,
      countryLabel,
      formatUnitPrice,
      formatNumber,
      onAmountChange,
      onCountryChange,
      onToggle,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.add-credits {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 22px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__head-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(148, 200, 64, 0.16);
    color: #629d03;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.4;
  }

  &__body {
    margin-top: 22px;
    padding-top: 22px;
    border-top: 1px dashed #e2e8f0;
  }

  // Shared AmountStepper component spacing
  &__stepper {
    margin-bottom: 22px;
  }

  // Estimation head (with dropdown)
  &__estim-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  // Country dropdown — compact, inside estim panel
  :deep(.add-credits__country) {
    height: 34px;
    min-width: 160px;
    border-radius: 100px;
    border: 1px solid #d6e3c1;
    background: #fff;
    align-items: center;

    .p-dropdown-label {
      padding: 0 12px;
      display: flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #0f172a;
    }

    .p-dropdown-trigger {
      width: 32px;
      color: #629d03;
    }

    &:hover {
      border-color: $brand-color-primary;
    }
  }

  // Estimation panel
  &__estim {
    background: linear-gradient(135deg, #fafff5 0%, #ffffff 100%);
    border: 1px solid #ecf3e3;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 14px;
  }

  &__estim-eyebrow {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #629d03;
  }

  &__estim-quantity {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
  }

  &__estim-sms-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: $brand-color-primary;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.375rem;
    flex-shrink: 0;
    box-shadow: 0 4px 12px -4px rgba(148, 200, 64, 0.55);
  }

  &__estim-qty-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__estim-qty-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  &__estim-qty-num {
    font-size: 2.25rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &__estim-sms-pill {
    background: #0f172a;
    color: #fff;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__estim-qty-caption {
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.4;
  }

  &__estim-prices {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__estim-price {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__estim-price-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    font-weight: 600;
  }

  &__estim-price-value {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;

    &--inplan {
      color: #629d03;
    }

    &--strike {
      color: #94a3b8;
      text-decoration: line-through;
      text-decoration-color: #cbd5e1;
      font-weight: 500;
    }
  }

  &__estim-price-unit {
    font-size: 0.6875rem;
    color: #94a3b8;
    font-weight: 500;
    margin-left: 2px;
  }

  &__estim-vs {
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    padding: 0 4px;
  }

  &__estim-saving {
    margin-left: auto;
    background: #0f172a;
    color: #fff;
    padding: 6px 12px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      color: #c1e07e;
    }
  }

  // Info note
  &__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #f6faff;
    border-radius: 10px;
    padding: 12px 14px;
    color: #475569;
    font-size: 0.8125rem;
    line-height: 1.5;

    i {
      color: #1d4ed8;
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.add-credits-expand-enter-active,
.add-credits-expand-leave-active {
  overflow: hidden;
  transition: max-height 240ms ease, opacity 200ms ease;
  max-height: 800px;
}

.add-credits-expand-enter-from,
.add-credits-expand-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>
